import { useCallback, useEffect, FC } from 'react'
import styled from 'styled-components'
import useEmblaCarousel from 'embla-carousel-react'

import { useMediaQueryContext } from 'contexts/MediaQueryContext'
import { media, mediaMax } from 'styles/media'

import LazyImage from '../display/LazyImage'
import ChevronDownIcon from '../icons/ChevronDown'
import LinkOrA from '../cards/shared/LinkOrA'
import { Arrow, Wrapper, SliderWrapper } from '../cards/shared/CardSlider'

import HorizontallyScrollable from './HorizontallyScrollable'
import StoryCredit from './StoryCredit'

const StoryImage = styled(LazyImage)`
  border-radius: 8px;
  max-height: 640px;

  ${mediaMax.md`
    max-height: unset;
    height: 50vh;
  `}
`

const Slide = styled.div<{ margin: number }>`
  width: 65vw;
  max-width: 1200px;
  margin-left: ${({ margin }) => margin}px;

  ${mediaMax.md`
    width: 80vw;
  `}

  & > *:last-child {
    margin-top: 20px;
  }
`

const EmblaViewport = styled.div`
  overflow: hidden;
`

const EmblaContainer = styled.div`
  display: flex;
  gap: 40px;
`

const EmblaSlide = styled.div`
  flex: 0 0 auto;
  min-width: 0;
`

const Outer = styled.div`
  position: relative;
  min-height: 320px;

  ${media.md`
    min-height: 770px;
  `}
`

type Story = {
  id: string
  url: string
  title: string
  promoImage: {
    large: string
  }
  user: {
    id: string
    firstName: string
    avatar: {
      thumbLg: string
    }
  }
}

const StoriesCarousel: FC<{
  stories: Story[]
}> = ({ stories }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'start' })
  const { md: isLargerThanMd } = useMediaQueryContext()
  const isSmallerThanMd = !isLargerThanMd

  const goNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi])

  const goPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])

  // Re-init when stories change
  useEffect(() => {
    if (emblaApi) emblaApi.reInit()
  }, [emblaApi, stories])

  if (isLargerThanMd) {
    return (
      <Outer>
        <Wrapper>
          <SliderWrapper>
            <EmblaViewport ref={emblaRef}>
              <EmblaContainer>
                {stories.map(({ id, promoImage, title, url, user }) => (
                  <EmblaSlide key={id}>
                    <Slide margin={isSmallerThanMd ? 0 : 40}>
                      <LinkOrA asLink={isSmallerThanMd} to={url}>
                        <StoryImage data-src={promoImage.large} />
                        <noscript>
                          <StoryImage src={promoImage.large} />
                        </noscript>
                      </LinkOrA>
                      <StoryCredit
                        avatar={user.avatar}
                        firstName={user.firstName}
                        storyTitle={title}
                        storyUrl={url}
                      />
                    </Slide>
                  </EmblaSlide>
                ))}
              </EmblaContainer>
            </EmblaViewport>
          </SliderWrapper>
          <>
            <Arrow onClick={goPrev} show left style={{ left: 60, top: '39%' }}>
              <ChevronDownIcon />
            </Arrow>
            <Arrow
              onClick={goNext}
              show
              right
              style={{ right: 60, top: '39%' }}
            >
              <ChevronDownIcon />
            </Arrow>
          </>
        </Wrapper>
      </Outer>
    )
  }

  return (
    <HorizontallyScrollable>
      {stories.map(({ id, promoImage, title, url, user }) => (
        <Slide key={id} margin={isSmallerThanMd ? 0 : 40}>
          <LinkOrA asLink={isSmallerThanMd} to={url}>
            <StoryImage data-src={promoImage.large} />
            <noscript>
              <StoryImage src={promoImage.large} />
            </noscript>
          </LinkOrA>
          <StoryCredit
            avatar={user.avatar}
            firstName={user.firstName}
            storyTitle={title}
            storyUrl={url}
          />
        </Slide>
      ))}
    </HorizontallyScrollable>
  )
}

export default StoriesCarousel
